import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getStatistics, exportStatistics } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BarChart3, Building2, Ticket, TrendingUp, Users, Calendar, Filter, Download } from 'lucide-react';
import AdminSidebar from '@/components/AdminSidebar';

const AdminStatistics = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('all');

  const periods = [
    { value: 'week', label: 'Última Semana' },
    { value: 'month', label: 'Último Mes' },
    { value: '3months', label: 'Últimos 3 Meses' },
    { value: 'all', label: 'Todo el Tiempo' },
  ];

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    loadStatistics();
  }, [isAuthenticated, navigate, selectedPeriod]);

  const loadStatistics = async () => {
    setLoading(true);
    try {
      const period = selectedPeriod === 'all' ? null : selectedPeriod;
      const response = await getStatistics(period);
      setStats(response.data);
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const period = selectedPeriod === 'all' ? null : selectedPeriod;
      await exportStatistics(period);
    } catch (error) {
      console.error('Error exporting:', error);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#F8F9FA] grid grid-cols-1 md:grid-cols-[240px_1fr]">
      <AdminSidebar />
      
      <main className="p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="statistics-title">
                Estadísticas de Uso
              </h1>
              <p className="text-[#64748B] mt-1">
                Análisis del uso de cupones por empresas
              </p>
            </div>
            
            {/* Period Filter and Export */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Filter className="w-4 h-4 text-[#64748B]" />
                {periods.map((period) => (
                  <Button
                    key={period.value}
                    variant={selectedPeriod === period.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedPeriod(period.value)}
                    className={selectedPeriod === period.value ? 'btn-primary' : ''}
                    data-testid={`filter-${period.value}`}
                  >
                    {period.label}
                  </Button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={exporting || !stats || stats.total_stats.total_uses === 0}
                className="flex items-center gap-2"
                data-testid="export-btn"
              >
                <Download className="w-4 h-4" />
                {exporting ? 'Exportando...' : 'Exportar Excel'}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="spinner"></div>
            </div>
          ) : stats ? (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#64748B]">Total Empresas</p>
                        <p className="text-3xl font-bold text-[#0F172A]">{stats.total_stats.total_companies}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-[#d1223b]/10 flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-[#d1223b]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#64748B]">Total Cupones</p>
                        <p className="text-3xl font-bold text-[#0F172A]">{stats.total_stats.total_coupons}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-[#fcd605]/20 flex items-center justify-center">
                        <Ticket className="w-6 h-6 text-[#fcd605]" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#64748B]">Total Usos</p>
                        <p className="text-3xl font-bold text-[#0F172A]">{stats.total_stats.total_uses}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-[#64748B]">Empresas con Usos</p>
                        <p className="text-3xl font-bold text-[#0F172A]">{stats.total_stats.companies_with_uses}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <Users className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Company Statistics */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Coupon Usage by Company (Offering) */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <BarChart3 className="w-5 h-5 text-[#d1223b]" />
                      Uso de Cupones por Empresa
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {stats.company_stats
                        .filter(c => c.total_coupons > 0)
                        .sort((a, b) => b.total_uses - a.total_uses)
                        .map((company) => (
                          <div key={company.company_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-white flex items-center justify-center flex-shrink-0">
                              {company.company_logo ? (
                                <img 
                                  src={company.company_logo} 
                                  alt={company.company_name}
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = `https://placehold.co/40x40/f1f5f9/d1223b?text=${company.company_name.substring(0, 2)}`;
                                  }}
                                />
                              ) : (
                                <span className="text-xs font-bold text-[#d1223b]">{company.company_name.substring(0, 2)}</span>
                              )}
                            </div>
                            <div className="flex-grow min-w-0">
                              <p className="font-medium text-[#0F172A] truncate">{company.company_name}</p>
                              <p className="text-xs text-[#64748B]">{company.total_coupons} cupones</p>
                            </div>
                            <Badge className={company.total_uses > 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                              {company.total_uses} usos
                            </Badge>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Companies Using Coupons */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <Users className="w-5 h-5 text-[#d1223b]" />
                      Empresas que Han Usado Cupones
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {stats.user_company_stats.length > 0 ? (
                        stats.user_company_stats
                          .sort((a, b) => b.total_uses - a.total_uses)
                          .map((company) => (
                            <div key={company.company_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                              <p className="font-medium text-[#0F172A]">{company.company_name}</p>
                              <Badge className="bg-[#d1223b]/10 text-[#d1223b]">
                                {company.total_uses} cupones usados
                              </Badge>
                            </div>
                          ))
                      ) : (
                        <div className="text-center py-8 text-[#64748B]">
                          <Users className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                          <p>Aún no hay registros de uso</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recent Usage Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <Calendar className="w-5 h-5 text-[#d1223b]" />
                    Últimos Usos de Cupones
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {stats.recent_usages.length > 0 ? (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Fecha y Hora</TableHead>
                            <TableHead>Cupón</TableHead>
                            <TableHead>Empresa Oferente</TableHead>
                            <TableHead>Usado Por</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {stats.recent_usages.map((usage) => (
                            <TableRow key={usage.id}>
                              <TableCell className="text-sm">
                                {formatDate(usage.used_at)}
                              </TableCell>
                              <TableCell className="font-medium">{usage.coupon_title}</TableCell>
                              <TableCell>{usage.company_name}</TableCell>
                              <TableCell>
                                <Badge variant="outline">{usage.used_by_company_name}</Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-12 text-[#64748B]">
                      <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-2" />
                      <p>Aún no hay registros de uso de cupones</p>
                      <p className="text-sm mt-1">Los usos aparecerán aquí cuando los empleados canjeen cupones</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="text-center py-20 text-[#64748B]">
              <BarChart3 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p>Error al cargar las estadísticas</p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminStatistics;
